import { validator } from '@atlaskit/adf-utils/validator';
import { Fragment, type Node as PMNode } from '@atlaskit/editor-prosemirror/model';

let validateCaption: ReturnType<typeof validator> | undefined;
const CAPTION_DOCUMENT_KEYS = new Set(['type', 'version', 'content']);

/** Resolve an unambiguous caption in its existing media parent. */
export function findCaptionReplacementTarget(doc: PMNode, localId: string): PMNode | undefined {
	let target: PMNode | undefined;
	let matches = 0;
	doc.descendants((node, _pos, parent) => {
		if (node.attrs.localId === localId) {
			matches++;
			if (
				node.type.name === 'caption' &&
				node.isTextblock &&
				node.type.spec.selectable === false &&
				parent?.type.name === 'mediaSingle' &&
				parent.type.validContent(parent.content)
			) {
				target = node;
			}
		}
	});
	return matches === 1 ? target : undefined;
}

type CaptionReplacementSnapshot = { rejected: false; value: string } | { rejected: true };

const isCaptionReplacementShape = (snapshot: unknown): boolean => {
	if (!snapshot || typeof snapshot !== 'object') {
		return false;
	}
	const root = snapshot as { content?: unknown; type?: unknown };
	return (
		root.type === 'caption' ||
		(Array.isArray(root.content) &&
			root.content.some(
				(node: unknown) =>
					node !== null &&
					typeof node === 'object' &&
					(node as { type?: unknown }).type === 'caption',
			))
	);
};

/** Classify caption-shaped snapshots without validating or normalizing their content. */
export function isCaptionReplacementSnapshot(fullValue: string): boolean {
	try {
		return isCaptionReplacementShape(JSON.parse(fullValue));
	} catch {
		return false;
	}
}

/** Validate caption inlines in their target schema before using a document-valid carrier. */
export function recoverCaptionReplacementSnapshot(
	fullValue: string,
	target: PMNode | undefined,
): CaptionReplacementSnapshot | undefined {
	let snapshot;
	try {
		snapshot = JSON.parse(fullValue);
	} catch {
		return target ? { rejected: true } : undefined;
	}
	if (!isCaptionReplacementShape(snapshot)) {
		return target ? { rejected: true } : undefined;
	}
	if (
		!target ||
		snapshot.type !== 'doc' ||
		Object.keys(snapshot).some((key) => !CAPTION_DOCUMENT_KEYS.has(key)) ||
		snapshot.version !== 1 ||
		!Array.isArray(snapshot.content) ||
		snapshot.content.length !== 1 ||
		snapshot.content[0]?.type !== 'caption' ||
		(snapshot.content[0].content !== undefined && !Array.isArray(snapshot.content[0].content))
	) {
		return { rejected: true };
	}
	try {
		validateCaption ??= validator();
		if (!validateCaption(snapshot.content[0]).valid || snapshot.content[0].marks?.length) {
			return { rejected: true };
		}
		const schema = target.type.schema;
		const caption = target.copy(Fragment.fromJSON(schema, snapshot.content[0].content));
		caption.check();
		let hasUnsupportedContent = false;
		caption.descendants((node) => {
			if (
				node.type.name === 'unsupportedInline' ||
				node.marks.some(
					(mark) =>
						mark.type.name === 'unsupportedMark' || mark.type.name === 'unsupportedNodeAttribute',
				)
			) {
				hasUnsupportedContent = true;
			}
		});
		if (hasUnsupportedContent) {
			return { rejected: true };
		}
		const paragraph = schema.nodes.paragraph.createChecked(null, caption.content);
		paragraph.check();
		// The processor preserves the command's caption-only range when opening the AI
		// experience. An open paragraph slice supplies inlines within that range.
		return {
			rejected: false,
			value: JSON.stringify({ type: 'doc', version: 1, content: [paragraph.toJSON()] }),
		};
	} catch {
		return { rejected: true };
	}
}
